import gql from 'graphql-tag';
import React, {useState} from 'react';

import {updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  GetResultSchemaQuery,
  GetResultSchemaQueryVariables,
} from 'analysis-tool/structured-data-mapping/__generated/GetResultSchemaQuery';
import {
  DataMappingState,
  TargetSpecificMappingState,
} from 'analysis-tool/structured-data-mapping/DataMappingTypes';
import {InternalSaveToResultsStepConfigurationView} from 'analysis-tool/structured-data-mapping/SaveToResultsStepConfigurationView';
import useDataMappingState from 'analysis-tool/structured-data-mapping/useDataMappingState';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {DEFAULT_DATA_FRAME_ROW_LIMIT, GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const GET_RESULT_SCHEMA_QUERY = gql`
  query GetResultSchemaQuery($schemaId: StrictString!) @skipAudit {
    resultSchema(id: $schemaId) {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ResultSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type SaveToResultsStepReconfigurationViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION>,
  'analysis' | 'clientStateId' | 'onCloseStepInitializationView' | 'step'
>;

export type InternalSaveToResultsStepReconfigurationViewProps = SaveToResultsStepReconfigurationViewProps & {
  defaultDataMappingState: DataMappingState;
  defaultTargetSpecificMappingState: TargetSpecificMappingState | null;
};

export function InternalSaveToResultsStepReconfigurationView(
  props: InternalSaveToResultsStepReconfigurationViewProps
): JSX.Element {
  const inputDataFrameId = props.step.inputStepDatas[0].dataFrames[0].id;

  const [dataMappingState, setDataMappingState, resetDataMappingState] = useDataMappingState(
    props.clientStateId,
    inputDataFrameId,
    props.defaultDataMappingState
  );
  const [targetSpecificMappingState, setTargetSpecificMappingState] =
    useState<TargetSpecificMappingState | null>(props.defaultTargetSpecificMappingState);

  const closeViewAndReturnToOutputView = (): void => {
    props.onCloseStepInitializationView({
      type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT,
      stepId: props.step.id,
    });
  };

  const rerunStep = async (
    dataMappingState: DataMappingState,
    targetSpecificMappingState: TargetSpecificMappingState | null
  ): Promise<void> => {
    await updateAndRunAnalysisStep(props.analysis.id, {
      stepId: props.step.id,
      stepConfig: {
        schemaId: dataMappingState.resultSchema!.id,
        columnMapping: dataMappingState.mappings,
        targetSpecificMapping: targetSpecificMappingState,
      },
    });
    closeViewAndReturnToOutputView();
  };

  const onCancel = (): void => {
    closeViewAndReturnToOutputView();
    resetDataMappingState();
  };

  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: inputDataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          <InternalSaveToResultsStepConfigurationView
            clientStateId={props.clientStateId}
            inputDataFrame={dataFrame.dataFrameJson}
            dataMappingState={dataMappingState}
            setDataMappingState={setDataMappingState}
            targetSpecificMappingState={targetSpecificMappingState}
            setTargetSpecificMappingState={setTargetSpecificMappingState}
            addSaveToResultsStep={() => rerunStep(dataMappingState, targetSpecificMappingState)}
            onCancel={onCancel}
          />
        );
      }}
    </BenchlingQuery>
  );
}

/**
 * TODO(BNCH-60583): Currently there is inconsistent naming for the views to (1) initially configure a step,
 * (2) view the configuration of a completed step, and (3) re-configure a step. See
 * `INSIGHTS_BLOCK_DATASET_CONFIGURATION` versus `SAVE_TO_RESULTS_CONFIGURATION`. Standardize the terminology.
 */
export default function SaveToResultsStepReconfigurationView(
  props: SaveToResultsStepReconfigurationViewProps
): JSX.Element {
  assert(props.step.config != null, 'Expected step config to exist.');

  const {columnMapping, schemaId, targetSpecificMapping} = getPipelineStepConfig(
    props.step,
    PipelineStepType.SAVE_TO_RESULTS,
    2
  );

  return (
    <BenchlingQuery<GetResultSchemaQuery, GetResultSchemaQueryVariables>
      query={GET_RESULT_SCHEMA_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{schemaId}}
    >
      {({resultSchema}) => {
        assert(resultSchema != null, 'Querying for result schema unexpectedly failed.');
        assert(!isInaccessible(resultSchema), 'Expected result schema to be accessible.');

        const defaultDataMappingState: DataMappingState = {
          resultSchema: graphQLToREST('ResultSchema', resultSchema),
          mappings: columnMapping,
        };

        return (
          <InternalSaveToResultsStepReconfigurationView
            {...props}
            defaultDataMappingState={defaultDataMappingState}
            defaultTargetSpecificMappingState={targetSpecificMapping as TargetSpecificMappingState | null}
          />
        );
      }}
    </BenchlingQuery>
  );
}
